import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import GestorPlantillas from '../components/GestorPlantillas'
import './PostVenta.css'

export default function PostVenta({ toast, comercioId }) {
  const [mensajes, setMensajes] = useState([])
  const [plantillas, setPlantillas] = useState([]) 
  const [planActual, setPlanActual] = useState('Basico')
  const [cargando, setCargando] = useState(true)
  const [pestanaActiva, setPestanaActiva] = useState('pendiente')

  const [msgAEditar, setMsgAEditar] = useState(null)
  const [textoEditable, setTextoEditable] = useState('')
  const [fechaEditable, setFechaEditable] = useState('')

  // 🚀 Usamos la variable de entorno centralizada
  const BACKEND_URL = process.env.REACT_APP_API_URL

  const cargarDatos = useCallback(async (mostrarLoaderCompleto = false) => {
    if (!comercioId) return
    if (mostrarLoaderCompleto) setCargando(true)

    try {
      const { data: comercio } = await supabase
        .from('comercios')
        .select('plan_actual')
        .eq('id', comercioId)
        .single()

      if (comercio) {
        setPlanActual(comercio.plan_actual || 'Basico')
      }

      const resPlantillas = await fetch(`${BACKEND_URL}/api/plantillas/${comercioId}`)
      if (resPlantillas.ok) {
        const dataP = await resPlantillas.json()
        setPlantillas(dataP)
      }

      const { data: cola } = await supabase
        .from('cola_mensajes_postventa')
        .select('*')
        .eq('comercio_id', comercioId)
        .order('fecha_envio', { ascending: true })

      if (cola) {
        setMensajes(cola)
      }
    } catch (error) {
      console.error('Error cargando módulo post-venta:', error)
      if (typeof toast === 'function') toast('Error al sincronizar el módulo de fidelización', 'error')
    } finally {
      setCargando(false)
    }
  }, [comercioId, toast, BACKEND_URL])

  useEffect(() => { 
    cargarDatos(true) 
  }, [comercioId, cargarDatos])

  const formatearFecha = (fechaString) => {
    if (!fechaString) return '—'
    const opciones = { day: 'numeric', month: 'short', year: 'numeric' }
    return new Date(fechaString + 'T00:00:00').toLocaleDateString('es-AR', opciones)
  }

  const obtenerEstrategiaBadge = (estrategia) => {
    const est = estrategia ? estrategia.toLowerCase() : ''
    if (est.includes('satisfaccion') || est === 'satisfaccion') {
      return { texto: `😊 ${estrategia.includes('Encuesta') ? estrategia : 'Satisfacción'}`, clase: 'badge-satisfaccion' }
    }
    if (est.includes('upselling') || est.includes('accesorios') || est === 'upselling') {
      return { texto: `${estrategia.includes('Upselling') ? estrategia : 'Upselling Accesorios'}`, clase: 'badge-upselling' }
    }
    if (est.includes('reseña') || est.includes('resena') || est === 'resena') {
      return { texto: `${estrategia.includes('Pedido') ? estrategia : 'Reseña Google'}`, clase: 'badge-resena' }
    }
    return { texto: `${estrategia || 'Post-Venta'}`, clase: 'badge-default' }
  }

  const aplicarPlantillaEnModal = (plantillaObjeto) => {
    if (!msgAEditar) return
    const nombre = msgAEditar.cliente_nombre.split(' ')[0]
    const equipos = msgAEditar.equipos_detalle

    const textoCrudo = plantillaObjeto.texto || ''
    const nuevoTexto = textoCrudo
      .replace(/{nombre}/g, nombre)
      .replace(/{equipo}/g, equipos)

    setTextoEditable(nuevoTexto)
  }

  const abrirModalEdicion = (msg) => {
    setMsgAEditar(msg)
    setTextoEditable(msg.mensaje_texto || `¡Hola ${msg.cliente_nombre}! Gracias por tu compra de ${msg.equipos_detalle}.`)
    setFechaEditable(msg.fecha_envio)
  }

  const guardarCambiosPostVenta = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/postventa/${msgAEditar.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mensaje_texto: textoEditable,
          fecha_envio: fechaEditable
        })
      })

      if (res.ok) {
        if (typeof toast === 'function') toast('¡Campaña personalizada guardada con éxito!', 'success')
        setMsgAEditar(null)
        cargarDatos(false) 
      } else {
        if (typeof toast === 'function') toast('No se pudo actualizar la campaña', 'error')
      }
    } catch (e) {
      if (typeof toast === 'function') toast('Error de conexión con el servidor', 'error')
    }
  }

  const esPlanRestringido = !['pro', 'vip'].includes(planActual.toLowerCase())

  const mensajesBase = esPlanRestringido && mensajes.length === 0 
    ? [
        { id: 1, cliente_nombre: 'Carlos Thompson', telefono: '549112345678', equipos_detalle: 'iPhone 14 Pro (128 GB)', estrategia: 'satisfaccion', fecha_envio: '2026-06-05', estado: 'pendiente', mensaje_texto: '¡Hola Carlos! Queríamos saber cómo te estás sintiendo...' },
        { id: 2, cliente_nombre: 'Marta Gomez', telefono: '549118765432', equipos_detalle: 'iPhone 13 (256 GB)', estrategia: 'upselling', fecha_envio: '2026-06-09', estado: 'pendiente', mensaje_texto: '¡Hola Marta! Esperamos que estés disfrutando tu iPhone 13...' }
      ]
    : mensajes

  const mensajesAMostrar = esPlanRestringido 
    ? mensajesBase 
    : mensajesBase.filter(m => 
        pestanaActiva === 'pendiente' ? m.estado === 'pendiente' : m.estado !== 'pendiente'
      )

  return (
    <div className="pv-container">
      <div className="pv-header">
        <div className="pv-title-group">
          <h1 className="pv-title">Fidelización Post-Venta</h1>
          <p className="pv-subtitle">Gestioná y personalizá las campañas automáticas de tus clientes</p>
        </div>

        <div className="pv-tabs">
          <button className={`pv-tab ${pestanaActiva === 'pendiente' ? 'activa' : ''}`} onClick={() => setPestanaActiva('pendiente')}>
            Programados ({mensajesBase.filter(m => m.estado === 'pendiente').length})
          </button>
          <button className={`pv-tab ${pestanaActiva === 'historial' ? 'activa' : ''}`} onClick={() => setPestanaActiva('historial')}>
            Historial ({mensajesBase.filter(m => m.estado !== 'pendiente').length})
          </button>
          <button className={`pv-tab ${pestanaActiva === 'plantillas' ? 'activa' : ''}`} onClick={() => setPestanaActiva('plantillas')}>
            Mis Estrategias
          </button>
        </div>
      </div>

      {cargando ? (
        <div className="pv-loader">Cargando campañas...</div>
      ) : (
        <div className="pv-content-wrapper">
          {esPlanRestringido && (
            <div className="pv-paywall-overlay">
              <div className="pv-paywall-card">
                <div className="pv-paywall-icon">🚀</div>
                <h3>Activá el módulo Post-Venta Automático</h3>
                <p>Dejá de agendar recordatorios a mano. Con los planes <strong>PRO</strong> o <strong>VIP</strong>, el sistema detecta cada venta y envía campañas automáticas de satisfacción, venta cruzada o reseñas en Google.</p>
                
                <button 
                  className="btn-pv-upgrade" 
                  onClick={() => {
                    if (typeof toast === 'function') toast('Redirigiendo a suscripciones...', 'info');
                    // 🚀 Ruta relativa para que funcione local y en prod
                    window.location.href = '/suscripcion'; 
                  }}
                >
                  Mejorar Plan Ahora
                </button>
              </div>
            </div>
          )}

          {pestanaActiva === 'plantillas' && !esPlanRestringido ? (
            <div>
              <GestorPlantillas toast={toast} comercioId={comercioId} />
            </div>
          ) : (
            <div className={`pv-cards-grid ${esPlanRestringido ? 'pv-blurred' : ''}`}>
              {mensajesAMostrar.length === 0 ? (
                <div className="pv-empty">
                  <p className="pv-empty-title">No hay mensajes en esta sección</p>
                  <p className="pv-empty-subtitle">Las campañas aparecerán ordenadas automáticamente por fecha de salida.</p>
                </div>
              ) : (
                mensajesAMostrar.map((msg) => {
                  const est = obtenerEstrategiaBadge(msg.estrategia)
                  return (
                    <div key={msg.id} className={`pv-card-item status-${msg.estado}`}>
                      <div className="pv-card-header">
                        <span className={`pv-badge ${est.clase}`}>{est.texto}</span>
                        <span className={`pv-status-dot ${msg.estado}`} title={`Estado: ${msg.estado}`}>{msg.estado.toUpperCase()}</span>
                      </div>
                      <div className="pv-card-body">
                        <h4 className="pv-customer-name">{msg.cliente_nombre}</h4>
                        <p className="pv-customer-phone">📱 {msg.telefono}</p>
                        <div className="pv-device-box">
                          <span className="pv-device-label">Equipo vendido:</span>
                          <p className="pv-device-name">{msg.equipos_detalle}</p>
                        </div>
                        <div className="pv-message-preview-box">
                          <span className="pv-message-preview-label">Texto a enviar:</span>
                          <p className="pv-message-preview-text">"{msg.mensaje_texto || 'Generando texto de campaña por defecto...'}"</p>
                        </div>
                      </div>
                      <div className="pv-card-footer">
                        <div className="pv-date-group">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                          <span>Envío: <strong>{formatearFecha(msg.fecha_envio)}</strong></span>
                        </div>
                        {msg.estado === 'pendiente' && !esPlanRestringido && (
                          <button className="btn-pv-action-edit" onClick={() => abrirModalEdicion(msg)}>Ajustar Mensaje</button>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* MODAL DE EDICIÓN */}
      {msgAEditar && (
        <div className="pv-modal-overlay">
          <div className="pv-modal-card">
            <div className="pv-modal-header">
              <h3>🎛️ Modificar Campaña de Post-Venta</h3>
              <p>Cliente: <strong>{msgAEditar.cliente_nombre}</strong></p>
            </div>
            
            <div className="pv-modal-body">
              <div className="pv-form-group">
                <label className="pv-label">Cambiar por una plantilla rápida de tu negocio:</label>
                <div className="pv-template-grid-buttons">
                  {plantillas.map(p => (
                    <button key={p.id} type="button" className="btn-pv-tpl" onClick={() => aplicarPlantillaEnModal(p)}>
                      {p.nombre.split(' ')[0] || 'Plantilla'}
                    </button>
                  ))}
                  {plantillas.length === 0 && <span style={{fontSize: '12px', color: '#888'}}>No hay estrategias guardadas.</span>}
                </div>
              </div>

              <div className="pv-form-group">
                <label className="pv-label">Mensaje personalizado de WhatsApp:</label>
                <textarea className="pv-textarea" value={textoEditable} onChange={(e) => setTextoEditable(e.target.value)} rows="5" />
              </div>

              <div className="pv-form-group">
                <label className="pv-label">Fecha programada para el envío:</label>
                <input type="date" className="pv-input-date" value={fechaEditable} onChange={(e) => setFechaEditable(e.target.value)} />
              </div>
            </div>

            <div className="pv-modal-footer">
              <button className="btn-pv-modal-cancelar" onClick={() => setMsgAEditar(null)}>Cancelar</button>
              <button className="btn-pv-modal-guardar" onClick={guardarCambiosPostVenta}>Guardar Cambios</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}